/**
 * Las analíticas de UNA oferta.
 *
 * ── Por qué no llama a nada ──────────────────────────────────────────────────
 * Se calculan con los cobros que la ficha ya tiene cargados para su tabla de
 * ventas. No hay una segunda petición ni una ruta nueva: lo que se enseña aquí
 * —cuánto entró, cuántas ventas, a cuánto sale cada una, cuánta gente distinta y
 * cómo se reparte en el tiempo— sale entero de esas filas.
 *
 * Y por eso es exacto en el sentido que importa: la tabla de al lado y estas
 * cifras cuentan lo mismo, porque son la misma lista contada de dos maneras. Una
 * consulta aparte podría discrepar de lo que se ve un renglón más abajo, que es
 * la peor cara que puede poner una analítica.
 *
 * ── Neto y bruto ─────────────────────────────────────────────────────────────
 * El dinero se cuenta NETO: lo cobrado menos lo devuelto. La cuenta de ventas no
 * descuenta las devueltas, porque son compras que ocurrieron. Las dos cifras
 * pueden no cuadrar entre sí y eso es correcto — es la misma regla que sigue el
 * resumen de la oferta.
 *
 * Sin tabla de cifras al pie: la tuvo un rato —mes a mes, con la cuenta y el
 * importe— y sobraba. Lo que se viene a ver aquí es si esto se mueve y cuánto
 * lleva, y eso ya lo dicen las tarjetas de arriba y la forma de la gráfica; el
 * detalle venta a venta está en la pestaña de al lado, con su nombre y su fecha.
 *
 * ── Una moneda ───────────────────────────────────────────────────────────────
 * Se manda la que más facturó. Dos monedas no se suman en un número, así que las
 * demás no se inventan: se dice cuántas quedan fuera, igual que hace la lista.
 */
import { useMemo, useState } from 'react'
import { IconCoins, IconReceipt, IconShoppingCart, IconUsers } from '@tabler/icons-react'
import { DateRangePicker } from '../DateRangePicker'
import { MetricChart, type MetricPoint } from '../MetricChart'
import { MoneyMetric, StatCard } from './analyticsShared'
import type { AnalyticsQuery, Order } from '../../../lib/api'
import { useI18n } from '../../../lib/i18n'

/** El día de un cobro en la zona de quien mira, que es la que lee el eje. */
function dayOf(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 10)
}

function addDays(day: string, delta: number): string {
  const date = new Date(`${day}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + delta)
  return date.toISOString().slice(0, 10)
}

type OfferAnalyticsWindow = { from: string; to: string; days: number }

function daysBetween(from: string, to: string): number {
  return Math.round((new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime()) / 86_400_000) + 1
}

/**
 * Resuelve los atajos del selector compartido para la ficha de una oferta.
 *
 * Aquí los rangos móviles INCLUYEN hoy. El Analytics general termina ayer para
 * comparar días completos; en esta ficha se viene a comprobar la venta que se
 * acaba de hacer, y dejarla fuera hacía parecer que el gráfico iba retrasado.
 */
function resolveWindow(query: AnalyticsQuery): OfferAnalyticsWindow {
  const today = dayOf(new Date().toISOString())
  const yesterday = addDays(today, -1)
  const span = (from: string, to: string): OfferAnalyticsWindow => ({ from, to, days: daysBetween(from, to) })

  if (query.range === 'custom' && query.from && query.to) {
    return query.from <= query.to ? span(query.from, query.to) : span(query.to, query.from)
  }

  switch (query.range) {
    case 'today': return span(today, today)
    case 'yesterday': return span(yesterday, yesterday)
    case '7d': return span(addDays(today, -6), today)
    case '30d': return span(addDays(today, -29), today)
    case '90d': return span(addDays(today, -89), today)
    case '12m': return span(addDays(today, -364), today)
    case 'lastWeek': {
      const dayOfWeek = new Date(`${today}T00:00:00Z`).getUTCDay()
      const mondayOffset = (dayOfWeek + 6) % 7
      const thisMonday = addDays(today, -mondayOffset)
      return span(addDays(thisMonday, -7), addDays(thisMonday, -1))
    }
    case 'lastMonth': {
      const current = new Date(`${today}T00:00:00Z`)
      const from = new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth() - 1, 1))
      const to = new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth(), 0))
      return span(from.toISOString().slice(0, 10), to.toISOString().slice(0, 10))
    }
    case 'lastYear': {
      const year = new Date(`${today}T00:00:00Z`).getUTCFullYear() - 1
      return span(`${year}-01-01`, `${year}-12-31`)
    }
    default: return span(addDays(today, -29), today)
  }
}

export function OfferAnalytics({ orders, loading, currency, free = false }: {
  /** Los cobros de ESTA oferta, ya filtrados por quien llama. */
  orders: Order[]
  loading: boolean
  /** La del proyecto: es la que se enseña mientras no haya ninguna venta. */
  currency: string
  /**
   * Si la oferta no cuesta nada.
   *
   * Cambia de qué van estos números. Una oferta gratuita deja pedidos de importe
   * cero —ver `freeClaim`— así que las cifras de dinero serían cuatro ceros
   * seguidos: exactas y sin nada que decir. Lo que se quiere saber ahí es cuánta
   * gente se la llevó, así que manda la CUENTA: encabeza las tarjetas y es la
   * que dibuja la línea de la gráfica.
   */
  free?: boolean
}) {
  const { t } = useI18n()
  /* Mismo selector de Analíticas, pero sin comparar: esta ficha pregunta cómo
     le fue a UNA oferta en un tramo, no contra qué periodo compite. */
  const [query, setQuery] = useState<AnalyticsQuery>({ range: '30d', compare: 'none' })
  const window = useMemo(() => resolveWindow(query), [query])
  const scopedOrders = useMemo(() => orders.filter((order) => {
    const day = dayOf(order.createdAt)
    return Boolean(day) && day >= window.from && day <= window.to
  }), [orders, window])
  const changeRange = (next: AnalyticsQuery) => {
    setQuery({
      range: next.range,
      ...(next.range === 'custom' && next.from && next.to ? { from: next.from, to: next.to } : {}),
      compare: 'none',
    })
  }

  const stats = useMemo(() => {
    /* La moneda que manda: la que más ha facturado. Con una sola —lo normal— es
       ella y no hay nada que decidir. */
    const byCurrency = new Map<string, number>()
    for (const order of scopedOrders) {
      byCurrency.set(order.currency, (byCurrency.get(order.currency) ?? 0) + order.amountTotal)
    }
    const main = [...byCurrency.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? currency
    const mine = scopedOrders.filter((order) => order.currency === main)

    const revenue = mine.reduce((total, order) => total + order.amountTotal - order.amountRefunded, 0)
    const buyers = new Set(mine.map((order) => (order.buyerEmail ?? '').toLowerCase()).filter(Boolean)).size

    /* El ticket medio sobre lo VENDIDO y no sobre lo que quedó: divide el bruto
       entre las ventas, porque un reembolso no hace que aquella compra fuera más
       barata — hace que se deshiciera. */
    const gross = mine.reduce((total, order) => total + order.amountTotal, 0)
    const aov = mine.length > 0 ? Math.round(gross / mine.length) : 0

    return { main, mine, revenue, buyers, aov, otherCurrencies: Math.max(0, byCurrency.size - 1) }
  }, [scopedOrders, currency])

  /**
   * La serie del gráfico: un punto por día, sin huecos, dentro del tramo
   * elegido. Así «30 días» conserva su línea previa aun si hoy fue la primera
   * venta, y una fecha personalizada no filtra sólo las tarjetas de arriba.
   *
   * Los días sin ventas tienen que existir y valer cero. Sin ellos, dos ventas
   * separadas por tres semanas salen pegadas y el dibujo miente sobre el ritmo.
   *
   */
  const series = useMemo<MetricPoint[]>(() => {
    if (orders.length === 0) return []

    const revenueBy = new Map<string, number>()
    const countBy = new Map<string, number>()
    for (const order of stats.mine) {
      const day = dayOf(order.createdAt)
      if (!day) continue
      revenueBy.set(day, (revenueBy.get(day) ?? 0) + order.amountTotal - order.amountRefunded)
      countBy.set(day, (countBy.get(day) ?? 0) + 1)
    }

    const points: MetricPoint[] = []
    for (let day = window.from; day <= window.to; day = addDays(day, 1)) {
      points.push({ day, primary: revenueBy.get(day) ?? 0, secondary: countBy.get(day) ?? 0 })
    }
    return points
  }, [orders.length, stats, window])

  const money = (amount: number) => <MoneyMetric amount={amount} currency={stats.main} />
  const cash = (value: number) => new Intl.NumberFormat(undefined, { style: 'currency', currency: stats.main.toUpperCase() }).format(value / 100)

  /* Gratis, la línea cuenta entregas y el dinero se va del globo: una segunda
     columna de ceros no informa de nada. */
  const chart = free
    ? {
      series: series.map((point) => ({ day: point.day, primary: point.secondary ?? 0 })),
      primaryLabel: t('offers.detail.analytics.claims'),
      secondaryLabel: null,
      formatPrimary: (value: number) => String(value),
    }
    : {
      series,
      primaryLabel: t('salesAn.revenue'),
      secondaryLabel: t('salesAn.orders'),
      formatPrimary: cash,
    }

  return (
    <div className="dash-ofd-analytics">
      <div className="dash-ofd-analytics-range">
        <DateRangePicker query={query} window={window} onChange={changeRange} />
      </div>
      {/*
        Las cuatro cifras, con los nombres y los iconos de Analíticas de venta:
        quien viene de allí no tiene que volver a aprenderse qué es cada una.

        Gratis cambian dos: el dinero no dice nada —todo vale cero— y su sitio lo
        ocupan la cuenta de entregas, que pasa a encabezar, y cuántas de ellas
        fueron a alguien que no había pasado antes.
      */}
      <div className="dash-ofd-analytics-stats">
        {free ? (
          <>
            <StatCard label={t('offers.detail.analytics.claims')} value={String(stats.mine.length)} icon={IconShoppingCart} loading={loading} />
            <StatCard label={t('salesAn.customers')} value={String(stats.buyers)} icon={IconUsers} loading={loading} />
          </>
        ) : (
          <>
            <StatCard label={t('salesAn.revenue')} value={money(stats.revenue)} icon={IconCoins} loading={loading} />
            <StatCard label={t('salesAn.orders')} value={String(stats.mine.length)} icon={IconShoppingCart} loading={loading} />
            <StatCard label={t('salesAn.aov')} value={money(stats.aov)} icon={IconReceipt} loading={loading} />
            {/* Personas distintas, por correo en minúsculas: quien compró dos
                veces es una, que es lo que se pregunta al mirar este número. */}
            <StatCard label={t('salesAn.customers')} value={String(stats.buyers)} icon={IconUsers} loading={loading} />
          </>
        )}
      </div>

      {/* La misma gráfica que el resto del panel. Sin ventas dice QUÉ falta y no
          sólo que está vacía: una oferta recién publicada es el caso normal, no
          un error. */}
      <MetricChart
        series={chart.series}
        loading={loading}
        title={t('offers.detail.analytics.chart')}
        subtitle={t('offers.detail.analytics.chartFoot')}
        primaryLabel={chart.primaryLabel}
        secondaryLabel={chart.secondaryLabel}
        formatPrimary={chart.formatPrimary}
        formatSecondary={(value) => String(value)}
        ariaLabel={t('offers.detail.analytics.chart')}
        emptyLabel={t(free ? 'offers.detail.analytics.emptyClaims' : 'offers.detail.analytics.emptySales')}
      />

      {/* Las monedas que se quedan fuera, dichas y no sumadas. */}
      {stats.otherCurrencies > 0 ? (
        <p className="dash-offer-card-foot">{t('offers.detail.analytics.otherCurrencies', { count: stats.otherCurrencies })}</p>
      ) : null}
    </div>
  )
}
